import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  StorefrontBootstrap,
  StorefrontFeatureKey,
} from "@emynor/storefront-sdk";
import { STOREFRONT_LIVE_PREVIEW_READY } from "@emynor/storefront-sdk";
import { configureMoney } from "./format";
import { hexColorChannels } from "./theme-color";
import { resolveStorefrontLanguage } from "./language";
import { appConfigFromBootstrap } from "./api";
import {
  applyStorefrontLivePreviewUpdate,
  isStorefrontLivePreviewUpdate,
} from "./preview-bridge";
import { storefrontBootstrapQuery } from "./queries";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import type { ResponsiveImageSource } from "./types";

const StorefrontContext = createContext<StorefrontBootstrap | null>(null);

export function StorefrontProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [livePreview, setLivePreview] = useState<StorefrontBootstrap | null>(
    null,
  );
  const query = useQuery(storefrontBootstrapQuery());
  const bootstrap = livePreview ?? query.data;
  const orderingPaused = bootstrap
    ? storefrontOrderingPaused(bootstrap)
    : false;
  const lang = resolveStorefrontLanguage({
    defaultLocale: bootstrap?.tenant.defaultLocale,
    enabledLocales: bootstrap?.tenant.enabledLocales,
    storedLocale:
      typeof window === "undefined"
        ? null
        : window.localStorage.getItem("emynor.storefront.locale"),
  });
  const copy = stateCopy(lang);
  const tenantLogo = bootstrap?.tenant.logoImage ?? bootstrap?.tenant.logoUrl ?? null;
  const tenantName = bootstrap ? localized(bootstrap.tenant.name, lang) : "";
  const refreshAvailability = async () => {
    await query.refetch();
    await queryClient.invalidateQueries({ queryKey: ["branches"] });
  };

  useEffect(() => {
    const source = query.data;
    if (source?.mode !== "preview" || window.parent === window) return;
    const receiveUpdate = (event: MessageEvent<unknown>) => {
      if (
        event.source !== window.parent ||
        !isStorefrontLivePreviewUpdate(event.data)
      )
        return;
      const updated = applyStorefrontLivePreviewUpdate(source, event.data);
      setLivePreview(updated);
    };
    window.addEventListener("message", receiveUpdate);
    window.parent.postMessage({ type: STOREFRONT_LIVE_PREVIEW_READY }, "*");
    return () => window.removeEventListener("message", receiveUpdate);
  }, [query.data, queryClient]);

  useEffect(() => {
    if (!bootstrap) return;
    if (bootstrap.mode !== "published" || !bootstrap.storefront) {
      const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]') ??
        document.head.appendChild(document.createElement("meta"));
      robots.name = "robots";
      robots.content = "noindex,follow";
    }
    configureMoney(bootstrap.tenant.currency);
    const favicon =
      document.querySelector<HTMLLinkElement>('link[rel~="icon"]') ??
      document.head.appendChild(document.createElement("link"));
    favicon.rel = "icon";
    favicon.href = bootstrap.tenant.faviconUrl ?? "/favicon.ico";
    const config = bootstrap.storefront?.configuration;
    const primary = hexColorChannels(stringValue(config?.["primaryColor"]));
    const accent = hexColorChannels(stringValue(config?.["accentColor"]));
    if (primary) {
      document.documentElement.style.setProperty("--primary", primary);
      document.documentElement.style.setProperty("--ring", primary);
      document.documentElement.style.setProperty("--aubergine", primary);
    }
    if (accent) document.documentElement.style.setProperty("--gold", accent);
  }, [bootstrap]);

  if (query.isPending)
    return <StatePage title={copy.loading} />;
  if (query.isError)
    return (
      <StatePage
        title={copy.error}
        description={errorMessage(lang)}
        retry={() => query.refetch()}
        retryLabel={copy.retry}
        logo={tenantLogo}
        logoAlt={tenantName}
      />
    );
  if (!bootstrap?.storefront || bootstrap.mode === "unpublished") {
    return (
      <StatePage
        title={copy.preparing}
        description={copy.checkBack}
        logo={tenantLogo}
        logoAlt={tenantName}
      />
    );
  }

  return (
    <StorefrontContext.Provider value={bootstrap}>
      {bootstrap.mode === "preview" ? (
        <div
          className="sticky top-0 z-[100] flex flex-wrap items-center justify-center gap-x-3 bg-gold px-4 py-2 text-center text-sm font-bold text-gold-foreground"
          role="status"
        >
          <span>{copy.preview}</span>
          <span className="font-medium">{copy.previewDisabled}</span>
        </div>
      ) : null}
      {orderingPaused ? (
        <div
          className="flex flex-wrap items-center justify-center gap-3 border-b border-gold/40 bg-gold/10 px-4 py-3 text-center text-sm"
          role="status"
        >
          <span>
            <strong>{copy.paused}</strong> {copy.pausedHelp}
          </span>
          {bootstrap.tenant.supportPhone ? (
            <a
              href={`tel:${bootstrap.tenant.supportPhone}`}
              className="min-h-10 rounded-full border border-current px-4 py-2 font-bold"
              dir="ltr"
            >
              {copy.callRestaurant}
            </a>
          ) : null}
          <button
            type="button"
            className="min-h-10 rounded-full border border-current px-4 font-bold"
            onClick={() => void refreshAvailability()}
          >
            {copy.retry}
          </button>
        </div>
      ) : null}
      {children}
    </StorefrontContext.Provider>
  );
}

export function useStorefront(): StorefrontBootstrap {
  const value = useContext(StorefrontContext);
  if (!value)
    throw new Error("useStorefront must be used inside StorefrontProvider");
  return value;
}

export function useStorefrontConfig() {
  const storefront = useStorefront();
  return useMemo(() => appConfigFromBootstrap(storefront), [storefront]);
}

export function featureEnabled(
  bootstrap: StorefrontBootstrap,
  key: StorefrontFeatureKey,
): boolean {
  return bootstrap.features[key]?.enabledForCustomers === true;
}

export function storefrontOrderingPaused(
  bootstrap: StorefrontBootstrap,
): boolean {
  return (
    bootstrap.tenant.orderingPaused ||
    bootstrap.settings?.onlineOrderingEnabled === false
  );
}

export function localized(
  value: Record<string, string> | undefined,
  locale: string,
): string {
  if (!value) return "";
  return (
    value[locale] ||
    value["ar"] ||
    value["en"] ||
    Object.values(value).find(Boolean) ||
    ""
  );
}

export function configLocalized(
  bootstrap: StorefrontBootstrap,
  key: string,
): Record<string, string> {
  const value = bootstrap.storefront?.configuration[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, string>)
    : {};
}

export function configAsset(
  bootstrap: StorefrontBootstrap,
  key: string,
): string | null {
  const id = bootstrap.storefront?.configuration[key];
  return typeof id === "string"
    ? (bootstrap.storefront?.assetUrls[id] ?? null)
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function errorMessage(lang: "ar" | "en"): string {
  return lang === "ar"
    ? "يرجى المحاولة مرة أخرى بعد قليل."
    : "Please try again shortly.";
}

function StatePage({
  title,
  description,
  retry,
  retryLabel,
  logo,
  logoAlt,
}: {
  title: string;
  description?: string;
  retry?: () => void;
  retryLabel?: string;
  logo?: ResponsiveImageSource | null;
  logoAlt?: string;
}) {
  return (
    <main className="pattern-levant grid min-h-screen place-items-center px-4">
      <section className="surface-card max-w-lg p-10 text-center">
        {logo ? (
          <span className="mx-auto mb-5 grid size-24 place-items-center overflow-hidden rounded-2xl border border-border bg-white p-2 shadow-sm">
            <ResponsiveImage
              source={logo}
              sizes="96px"
              alt={logoAlt ?? ""}
              className="max-h-full max-w-full object-contain"
              width={96}
              height={96}
            />
          </span>
        ) : null}
        <h1 className="text-3xl font-black">{title}</h1>
        {description ? (
          <p className="mt-3 text-muted-foreground">{description}</p>
        ) : null}
        {retry ? (
          <button
            type="button"
            className="mt-6 rounded-full bg-primary px-5 py-2.5 font-bold text-primary-foreground"
            onClick={retry}
          >
            {retryLabel}
          </button>
        ) : null}
      </section>
    </main>
  );
}

function stateCopy(lang: "ar" | "en") {
  return lang === "ar"
    ? {
        loading: "الرجاء الانتظار, جاري تحميل المطعم",
        error: "تعذّر تحميل المتجر",
        retry: "إعادة المحاولة",
        preparing: "المطعم قيد التجهيز",
        checkBack: "يرجى العودة قريباً.",
        preview: "معاينة المسودة",
        previewDisabled: "الحسابات والدفع والطلبات معطّلة في المعاينة.",
        paused: "الطلبات متوقفة مؤقتًا.",
        pausedHelp: "المطعم لا يستقبل طلبات جديدة الآن. حاول مرة أخرى لاحقًا.",
        callRestaurant: "اتصل بالمطعم",
      }
    : {
        loading: "Loading storefront…",
        error: "The storefront could not be loaded",
        retry: "Try again",
        preparing: "This storefront is being prepared",
        checkBack: "Please check back soon.",
        preview: "Draft preview",
        previewDisabled:
          "Accounts, payments, and orders are disabled in preview.",
        paused: "Ordering is temporarily paused.",
        pausedHelp:
          "The restaurant is not accepting new orders at the moment. Please try again later.",
        callRestaurant: "Call restaurant",
      };
}
